import { useRef, useState } from "react";
import { MAX_PROMPT_LENGTH } from "@hatch/shared";
import { PROMPT_GALLERY } from "../../lib/promptGallery.js";
import { DEFAULT_FRAMEWORK, FRAMEWORK_OPTIONS, FRAMEWORK_PICKER_VISIBLE, type Framework } from "../../lib/frameworks.js";
import { startBuild } from "../../lib/useSessionStream.js";
import {
  EggGlyph,
  HatchScreen,
  LandingEgg,
  Nest,
  ParentDino,
  Peak,
  perchOnNest,
  type NestShape,
} from "./Scenery.js";

/**
 * One prompt box, and a shelf of prompts to start from.
 *
 * Same promise as the classic skin's landing screen, and the same behaviour
 * behind it: no options, no configuration, and a gallery that fills the box
 * with editable text rather than selecting a template. Only the clothes differ.
 *
 * The row is flat and holds six. The shared collection groups twelve prompts —
 * Business, Personal, Community, Tools — and the classic skin shows those
 * headings, but this design has a single ungrouped row and no room for a second
 * one: twelve chips wrap onto the egg at ordinary laptop heights, which costs
 * the screen its whole centrepiece.
 *
 * So the selection is local and the text is not. `STARTERS` names titles, and
 * the prompts themselves still come from the one place they are written, which
 * is what stops the two skins drifting into two different sets of examples.
 */
export function PromptView({ onStarted }: { onStarted: (sessionId: string) => void }) {
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [framework, setFramework] = useState<Framework>(DEFAULT_FRAMEWORK);
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = prompt.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    const result = await startBuild(trimmed, framework);
    if ("error" in result) {
      setError(result.error);
      setSubmitting(false);
      return;
    }
    onStarted(result.sessionId);
  }

  /** Fill the box and hand the user the cursor — a start, not a commitment. */
  function choose(text: string) {
    setPrompt(text);
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    box.setSelectionRange(text.length, text.length);
  }

  const starters = resolveStarters();

  return (
    <HatchScreen>
      {/*
        The logo sign hangs alone. The design's two nav signs — "How it works"
        and "Examples" — pointed at pages this app does not have, and a hanging
        sign that goes nowhere is worse than no sign.
      */}
      <header className="flex items-start px-[clamp(16px,4vw,44px)]">
        <div className="flex origin-top flex-col items-center animate-[sway_5s_ease-in-out_infinite]">
          <Posts gap="gap-14" />
          <div className="flex -rotate-[1.5deg] items-center gap-2.5 rounded-md border-2 border-shell-rule bg-card px-[22px] py-3 shadow-[0_4px_0_rgb(62_50_38_/_0.12)]">
            <EggGlyph className="h-[27px] w-[22px] border-[2.5px]" />
            <span className="font-chewy text-3xl tracking-[0.03em] text-ember">Hatch</span>
          </div>
        </div>
      </header>

      {/*
        The bottom padding is the scenery's own height, so this column stops where
        the peak starts however big the peak has been drawn. `--peak-scale` is the
        ladder in index.css; `EGG_CROWN` is how far the egg's top sits above the
        group's base, and the group hangs 10px below it.

        Without this the chips crossed the top of the egg at 900px tall and the
        nest came down on the "Hatch it!" button at 640. With it the page gets
        longer instead, which is the right way for a one-screen composition to
        fail.
      */}
      <main
        className="mx-auto flex w-full max-w-[980px] flex-1 flex-col items-center gap-5 px-5 pt-[34px] sm:gap-[26px] sm:px-6"
        style={{ paddingBottom: `calc(${EGG_CROWN}px * var(--peak-scale) - 10px)` }}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          {/* `text-balance` rather than a smaller size on a phone: at 34px the
              line broke after "hatch" and left "it." alone on the second line,
              which is the one break this sentence cannot take. */}
          <h1 className="font-chewy text-[clamp(34px,7.5vw,56px)] leading-[1.08] font-normal text-balance text-bark">
            Describe it. We&rsquo;ll <span className="text-ember">hatch</span> it.
          </h1>
          <p className="max-w-[540px] text-[clamp(14.5px,4vw,18px)] leading-[1.55] font-semibold text-pretty text-bark-soft">
            One prompt goes in the shell. A few minutes later, out comes a live full-stack
            website &mdash; built and deployed while you watch.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="flex w-full max-w-[660px] -rotate-[0.4deg] flex-col gap-2.5 rounded-[10px] border-2 border-shell-rule bg-card px-[22px] pt-5 pb-3.5 shadow-[0_6px_0_rgb(62_50_38_/_0.12)]"
        >
          <label htmlFor="prompt" className="sr-only">
            Describe the website you want
          </label>
          <textarea
            id="prompt"
            ref={boxRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH))}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void submit(e);
            }}
            // Three rows on a phone and the design's four from `sm` up. A
            // four-row box is 25px of the scarcest thing on a 640px-tall screen,
            // and nobody has typed anything into it yet — the height is a promise
            // about how much you may write, not room anyone is using.
            rows={3}
            autoFocus
            disabled={submitting}
            placeholder="A site for…"
            className="w-full resize-none bg-transparent font-nunito text-[17px] leading-[1.5] font-semibold text-bark outline-none placeholder:text-bark-fainter disabled:opacity-60 sm:min-h-[102px]"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {/*
                The framework choice, in the skin's own vocabulary: a pill pair
                sharing the chip treatment the starters below use, rather than
                the classic skin's segmented control. Same options, same
                default, same shared source — only the look belongs to the skin.

                Not shown at present: FRAMEWORK_PICKER_VISIBLE in
                lib/frameworks.ts is false while a Svelte build cannot finish,
                so the form sends DEFAULT_FRAMEWORK. The markup stays for the
                day that flips back.
              */}
              {FRAMEWORK_PICKER_VISIBLE && (
                <div
                  role="radiogroup"
                  aria-label="Frontend framework"
                  className="flex gap-1"
                >
                  {FRAMEWORK_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={framework === option.value}
                      title={option.hint}
                      disabled={submitting}
                      onClick={() => setFramework(option.value)}
                      className={`cursor-pointer rounded-full px-3 py-1 font-nunito text-[12px] font-extrabold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        framework === option.value
                          ? "bg-bark text-shell"
                          : "bg-nest text-bark-fainter hover:text-bark-faint"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              <span className="text-[13px] font-bold tabular-nums text-bark-fainter">
                {prompt.length}/{MAX_PROMPT_LENGTH}
              </span>
            </div>
            {/*
              Disabled only while a submission is in flight, never for an empty
              box. The design's card opens empty and its button is at full
              strength; fading it would make the first thing the user sees a
              greyed-out call to action. An empty submit is caught by the guard
              in `submit` instead, which is what the design does too.
            */}
            <button
              type="submit"
              disabled={submitting}
              className="cursor-pointer rounded-lg bg-ember px-[30px] py-[11px] font-chewy text-xl tracking-[0.05em] text-shell shadow-[0_5px_0_var(--color-ember-shadow)] transition-transform duration-100 hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-[0_1px_0_var(--color-ember-shadow)] disabled:cursor-not-allowed"
            >
              {submitting ? "Hatching…" : "Hatch it!"}
            </button>
          </div>
        </form>

        {error && (
          // The design's card has no error slot. Rendered beneath it in the
          // design's own body type rather than importing a treatment from
          // somewhere else.
          <p role="alert" className="max-w-[660px] text-center text-sm font-bold text-ember">
            {error}
          </p>
        )}

        <section className="flex flex-col items-center gap-3" aria-labelledby="starters">
          <h2
            id="starters"
            className="text-[11.5px] font-extrabold tracking-[0.18em] uppercase text-bark-faint"
          >
            Or crack one of these open
          </h2>
          {/* Tighter chips on a phone: six of them wrap to three rows there, and
              a row saved is a row the scenery gets back. */}
          <div className="relative z-[2] flex flex-wrap justify-center gap-2 sm:gap-2.5">
            {starters.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => choose(item.prompt)}
                disabled={submitting}
                title={item.prompt}
                className="cursor-pointer rounded-lg border-2 border-shell-rule bg-card px-3 py-1.5 font-nunito text-[12.5px] font-extrabold text-bark-soft shadow-[0_3px_0_rgb(62_50_38_/_0.1)] transition-all duration-100 hover:-translate-y-px hover:border-ember hover:text-ember disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-[13.5px]"
              >
                {item.title}
              </button>
            ))}
          </div>
        </section>

        <Peak
          width={460}
          height={300}
          mountainHeight={250}
          capWidth={200}
          capHeight={86}
          capBottom={168}
          placement="page"
        >
          <Nest {...NEST} />
          {/*
            Drawn before the egg so the egg keeps the front of the nest. The
            parent stands slightly further back, and at this size his head
            reaches the egg's flank — with the order reversed it would be the
            bird's beak in front of the egg, which puts him in the nest rather
            than beside it.
          */}
          <ParentDino {...perchOnNest(NEST, 0.82)} scale={0.72} headTilt={-30} />
          <LandingEgg />
        </Peak>
      </main>

      {/*
        Sits above the cloud sea's z-1 strips on its own z-2, and pinned to the
        bottom of the page rather than the viewport — same reasoning as the peak
        above it, and it has to be the same choice as the peak or the two come
        apart. Identical while the content fits, since the skin root is
        `min-h-dvh`; on a 640px phone, where it does not, a fixed footer sat on
        top of the starter chips.
      */}
      <footer className="absolute inset-x-0 bottom-0 z-[2] px-[18px] pt-3 pb-5 text-center text-[12.5px] font-bold text-cloud-text">
        {/*
          The joke goes first on a phone. All three sentences ran to three lines
          at 360px, and the middle one crossed the mountain, where cloud-blue text
          on sandstone stops being readable. "No dinosaurs are harmed" is the one
          part nobody needs — the expiry warning and the attribution both are.
        */}
        <span className="hidden sm:inline">No dinosaurs are harmed. </span>
        Sites expire when the nest cools &mdash; roughly an hour. &middot; Runs on Xano
        ephemeral infrastructure
      </footer>
    </HatchScreen>
  );
}

/**
 * The six the design puts in its row, by title.
 *
 * Titles rather than copies of the prompts: the text lives in
 * `lib/promptGallery.ts` and is edited there for both skins. A rename upstream
 * turns into a failing test here rather than a chip that quietly vanishes.
 */
/**
 * The nest on the ridge, in `Peak` coordinates.
 *
 * Declared here rather than inline so the egg, the nest, and the parent standing
 * beside it all read from one set of numbers — see `perchOnNest`.
 */
const NEST: NestShape = {
  outerWidth: 150,
  outerHeight: 44,
  outerBottom: 238,
  innerWidth: 118,
  innerHeight: 32,
  innerBottom: 246,
};

/**
 * The top of the egg, in `Peak` coordinates — the highest point in the scenery,
 * and so the height the column above it has to keep clear. `LandingEgg` sits at
 * `bottom: 256` and stands 82px tall.
 */
const EGG_CROWN = 338;

export const STARTERS = [
  "Appointment booking",
  "Product catalogue",
  "Inventory tracker",
  "Kanban board",
  "Feedback board",
  "Resource directory",
] as const;

/**
 * The six, resolved against the shared gallery.
 *
 * A title that no longer exists is dropped rather than thrown on. Throwing here
 * happens during render, and with no error boundary above it that turns a
 * one-word edit to a prompt title — a content change, in shared code, made by
 * someone with no reason to look at this file — into a blank landing page that
 * nobody can start a build from. Five chips is a worse row; no chips at all is
 * an outage.
 *
 * The loud failure still exists, it just happens somewhere it costs nothing:
 * `HatchPromptView.test.tsx` asserts every title resolves, so a rename turns
 * red in CI instead of in production.
 */
function resolveStarters(): Array<{ title: string; prompt: string }> {
  const all = PROMPT_GALLERY.flatMap((group) => group.prompts);
  return STARTERS.map((title) => all.find((p) => p.title === title)).filter(
    (p): p is { title: string; prompt: string } => p !== undefined,
  );
}

/** The two posts a hanging sign swings from. */
function Posts({ gap }: { gap: string }) {
  return (
    <div aria-hidden className={`flex ${gap}`}>
      <div className="h-[34px] w-[3px] bg-nest" />
      <div className="h-[34px] w-[3px] bg-nest" />
    </div>
  );
}
